"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../../elements/modal';
import { Tab } from '../../elements/tab';
import { Accordion } from '../../elements/accordion';
import { createGeminiApiService } from '../../integrations/gemini-api.client';
import { FormEngine } from '../form/formengine';
import { FormValidationProvider } from '../form/formvalidator';
import * as FC from '../form/formcomponents';
import { usePixelatedConfig } from '../../config/config.client';
import siteInfoForm from './siteinfo-form.json';
import visualDesignForm from './visualdesignform.json';
import routesForm from './routes-form.json';
import servicesForm from './services-form.json';
import './ConfigBuilder.css';
const debug = false;
const VisualDesignVariable = {
    value: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    group: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
};
/**
 * ConfigBuilder — Interactive configuration editor for site-level settings (siteInfo, routes, visual design) with optional AI-assisted recommendations.
 *
 * @param {shape} [props.initialConfig] - Initial configuration object used to prepopulate the editor.
 * @param {function} [props.onSave] - Callback invoked when the user saves the configuration (receives the full config object).
 */
ConfigBuilder.propTypes = {
    /** Initial site configuration to edit */
    initialConfig: PropTypes.shape({
        siteInfo: PropTypes.shape({
            name: PropTypes.string.isRequired,
            author: PropTypes.string.isRequired,
            description: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
            email: PropTypes.string.isRequired,
            favicon: PropTypes.string.isRequired,
            favicon_sizes: PropTypes.string.isRequired,
            favicon_type: PropTypes.string.isRequired,
            theme_color: PropTypes.string.isRequired,
            background_color: PropTypes.string.isRequired,
            default_locale: PropTypes.string.isRequired,
            display: PropTypes.string.isRequired,
            image: PropTypes.string,
            image_height: PropTypes.string,
            image_width: PropTypes.string,
            telephone: PropTypes.string,
            address: PropTypes.shape({
                streetAddress: PropTypes.string.isRequired,
                addressLocality: PropTypes.string.isRequired,
                addressRegion: PropTypes.string.isRequired,
                postalCode: PropTypes.string.isRequired,
                addressCountry: PropTypes.string.isRequired,
            }),
            priceRange: PropTypes.string,
            sameAs: PropTypes.arrayOf(PropTypes.string.isRequired),
            keywords: PropTypes.string,
            openingHours: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.arrayOf(PropTypes.string),
                PropTypes.arrayOf(PropTypes.shape({
                    day: PropTypes.string.isRequired,
                    open: PropTypes.string,
                    close: PropTypes.string,
                    hours: PropTypes.string,
                    closed: PropTypes.bool,
                })),
            ]),
            copyrightYear: PropTypes.number,
            potentialAction: PropTypes.shape({
                '@type': PropTypes.string,
                target: PropTypes.string.isRequired,
                'query-input': PropTypes.string,
                queryInput: PropTypes.string,
            }),
            services: PropTypes.arrayOf(PropTypes.shape({
                name: PropTypes.string.isRequired,
                description: PropTypes.string.isRequired,
                short_description: PropTypes.string,
                url: PropTypes.string,
            })),
        }).isRequired,
        routes: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.string,
            path: PropTypes.string.isRequired,
            title: PropTypes.string,
            description: PropTypes.string,
            keywords: PropTypes.arrayOf(PropTypes.string),
            hidden: PropTypes.bool,
        }).isRequired).isRequired,
        visualdesign: PropTypes.shape({
            'primary-color': PropTypes.shape(VisualDesignVariable).isRequired,
            'secondary-color': PropTypes.shape(VisualDesignVariable).isRequired,
            'accent1-color': PropTypes.shape(VisualDesignVariable).isRequired,
            'accent2-color': PropTypes.shape(VisualDesignVariable).isRequired,
            'bg-color': PropTypes.shape(VisualDesignVariable).isRequired,
            'text-color': PropTypes.shape(VisualDesignVariable).isRequired,
            'header-font': PropTypes.shape(VisualDesignVariable).isRequired,
            'body-font': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size1-min': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size1-max': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size2-min': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size2-max': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size3-min': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size3-max': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size4-min': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size4-max': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size5-min': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size5-max': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size6-min': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-size6-max': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-min-screen': PropTypes.shape(VisualDesignVariable).isRequired,
            'font-max-screen': PropTypes.shape(VisualDesignVariable).isRequired,
        }).isRequired,
    }),
    /** Save callback to persist config changes */
    onSave: PropTypes.func,
};
export function ConfigBuilder(props) {
    const { initialConfig, onSave } = props;
    const pixelatedConfig = usePixelatedConfig() ?? {};
    const defaultConfig = {
        siteInfo: {
            ...pixelatedConfig.siteInfo,
            services: pixelatedConfig.siteInfo?.services || []
        },
        routes: [], // Start with empty routes, the JSON structure is different
        visualdesign: pixelatedConfig.visualdesign
    };
    const [config, setConfig] = useState({
        ...defaultConfig,
        ...initialConfig,
        siteInfo: { ...defaultConfig.siteInfo, ...initialConfig?.siteInfo },
        routes: (initialConfig?.routes || []),
        visualdesign: { ...defaultConfig.visualdesign, ...initialConfig?.visualdesign }
    });
    const [socialLinks, setSocialLinks] = useState(initialConfig?.siteInfo?.sameAs || ['']);
    const [isFormValid, setIsFormValid] = useState(false);
    // AI Recommendations state
    const [aiModalOpen, setAiModalOpen] = useState(false);
    const [currentRouteIndex, setCurrentRouteIndex] = useState(null);
    const [aiRecommendations, setAiRecommendations] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [acceptTitle, setAcceptTitle] = useState(false);
    const [acceptKeywords, setAcceptKeywords] = useState(false);
    const [acceptDescription, setAcceptDescription] = useState(false);
    // Validate form whenever config changes
    useEffect(() => {
        const siteInfo = config.siteInfo || {};
        const isValid = String(siteInfo.name || '').trim() !== '' &&
            String(siteInfo.author || '').trim() !== '' &&
            String(siteInfo.description || '').trim() !== '' &&
            String(siteInfo.url || '').trim() !== '' &&
            String(siteInfo.email || '').trim() !== '' &&
            // Basic email validation
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(siteInfo.email || '');
        setIsFormValid(isValid);
    }, [config]);
    // Handle AI modal visibility - now handled by Modal component isOpen prop
    // useEffect(() => {
    // 	console.log('AI modal effect running, aiModalOpen:', aiModalOpen);
    // 	if (aiModalOpen) {
    // 		const modal = document.getElementById('myModalai-recommendations');
    // 		console.log('Modal element found:', modal);
    // 		if (modal) {
    // 			modal.style.display = 'block';
    // 			console.log('Set modal display to block');
    // 		}
    // 	}
    // }, [aiModalOpen]);
    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const jsonContent = e.target?.result;
                const parsedConfig = JSON.parse(jsonContent);
                // Validate the structure
                if (parsedConfig.siteInfo && parsedConfig.routes) {
                    // Ensure keywords are arrays for all routes
                    const normalizedRoutes = parsedConfig.routes.map((route) => ({
                        ...route,
                        keywords: Array.isArray(route.keywords)
                            ? route.keywords
                            : (typeof route.keywords === 'string'
                                ? route.keywords.split(',').map((k) => k.trim()).filter((k) => k.length > 0)
                                : [])
                    }));
                    // Ensure services keywords/arrays are valid
                    const normalizedServices = (parsedConfig.siteInfo.services || []).map((service) => ({
                        ...service
                    }));
                    setConfig({
                        ...parsedConfig,
                        siteInfo: {
                            ...parsedConfig.siteInfo,
                            services: normalizedServices
                        },
                        routes: normalizedRoutes
                    });
                    setSocialLinks(parsedConfig.siteInfo.sameAs || ['']);
                }
                else {
                    alert('Invalid configuration file. Expected siteInfo and routes properties.');
                }
            }
            catch {
                alert('Error parsing JSON file. Please ensure it contains valid JSON.');
            }
        };
        reader.readAsText(file);
        // Reset the input
        event.target.value = '';
    };
    useEffect(() => {
        if (initialConfig) {
            // Ensure keywords are arrays for all routes
            const normalizedRoutes = (initialConfig.routes || []).map((route) => ({
                ...route,
                keywords: Array.isArray(route.keywords)
                    ? route.keywords
                    : (typeof route.keywords === 'string'
                        ? route.keywords.split(',').map((k) => k.trim()).filter((k) => k.length > 0)
                        : [])
            }));
            setConfig((prev) => ({
                siteInfo: { ...prev.siteInfo, ...initialConfig.siteInfo },
                routes: normalizedRoutes,
                visualdesign: initialConfig.visualdesign || prev.visualdesign || {}
            }));
            setSocialLinks(initialConfig.siteInfo?.sameAs || ['']);
        }
    }, [initialConfig]);
    // Prepare form data for FormEngine with current values
    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
    };
    const setNestedValue = (obj, path, value) => {
        const keys = path.split('.');
        const lastKey = keys.pop();
        const target = keys.reduce((current, key) => {
            if (!current[key])
                current[key] = {};
            return current[key];
        }, obj);
        target[lastKey] = value;
    };
    const formData = {
        fields: siteInfoForm.fields.map(field => ({
            ...field,
            props: {
                ...field.props,
                value: getNestedValue(config.siteInfo, field.props.name) || '',
                defaultValue: getNestedValue(config.siteInfo, field.props.name) || field.props.defaultValue || '',
                onChange: (value) => {
                    // Handle both direct values and event objects
                    let actualValue = value;
                    if (value && typeof value === 'object' && value.target) {
                        // It's an event object, extract the value
                        const target = value.target;
                        actualValue = target.type === 'checkbox' ? (target.checked ? target.value : '') : target.value;
                    }
                    setConfig((prev) => {
                        const newSiteInfo = { ...prev.siteInfo };
                        setNestedValue(newSiteInfo, field.props.name, actualValue);
                        return {
                            ...prev,
                            siteInfo: newSiteInfo
                        };
                    });
                }
            }
        }))
    };
    // Visual Design form data
    const visualFormData = {
        fields: (visualDesignForm.fields || []).map((field) => ({
            ...field,
            props: {
                ...field.props,
                value: (config.visualdesign && config.visualdesign[field.props.name]) ? (config.visualdesign[field.props.name].value ?? config.visualdesign[field.props.name]) : '',
                defaultValue: (config.visualdesign && config.visualdesign[field.props.name]) ? (config.visualdesign[field.props.name].value ?? config.visualdesign[field.props.name]) : field.props.defaultValue || '',
                onChange: (value) => {
                    // Handle both direct values and event objects
                    let actualValue = value;
                    if (value && typeof value === 'object' && value.target) {
                        actualValue = value.target.value;
                    }
                    setConfig((prev) => ({
                        ...prev,
                        visualdesign: {
                            ...(prev.visualdesign || {}),
                            [field.props.name]: {
                                ...(prev.visualdesign && prev.visualdesign[field.props.name]
                                    ? prev.visualdesign[field.props.name]
                                    : {}),
                                value: actualValue
                            }
                        }
                    }));
                }
            }
        }))
    };
    const handleFormSubmit = (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        const siteInfoData = {};
        // Extract form data
        for (const [key, value] of formData.entries()) {
            setNestedValue(siteInfoData, key, value);
        }
        // Update config with form data
        setConfig(prev => ({
            ...prev,
            siteInfo: {
                ...prev.siteInfo,
                ...siteInfoData,
                sameAs: socialLinks.filter(link => link.trim() !== '')
            }
        }));
    };
    const updateAddress = (field, value) => {
        setConfig(prev => ({
            ...prev,
            siteInfo: {
                ...prev.siteInfo,
                address: {
                    ...(prev.siteInfo.address || {}),
                    [field]: value
                }
            }
        }));
    };
    const addSocialLink = () => {
        setSocialLinks(prev => [...prev, '']);
    };
    const updateSocialLink = (index, value) => {
        setSocialLinks(prev => prev.map((link, i) => i === index ? value : link));
    };
    const removeSocialLink = (index) => {
        setSocialLinks(prev => prev.filter((_, i) => i !== index));
    };
    const addRoute = () => {
        setConfig(prev => ({
            ...prev,
            routes: [...prev.routes, { name: '', path: '', title: '', description: '', keywords: [], hidden: false }]
        }));
    };
    const updateRoute = (index, field, value) => {
        setConfig(prev => ({
            ...prev,
            routes: prev.routes.map((route, i) => i === index ? { ...route, [field]: value } : route)
        }));
    };
    const removeRoute = (index) => {
        setConfig(prev => ({
            ...prev,
            routes: prev.routes.filter((_, i) => i !== index)
        }));
    };
    const addService = () => {
        setConfig(prev => ({
            ...prev,
            siteInfo: {
                ...prev.siteInfo,
                services: [...(prev.siteInfo.services || []), { name: '', description: '', url: '' }]
            }
        }));
    };
    const updateService = (index, field, value) => {
        setConfig(prev => ({
            ...prev,
            siteInfo: {
                ...prev.siteInfo,
                services: (prev.siteInfo.services || []).map((service, i) => i === index ? { ...service, [field]: value } : service)
            }
        }));
    };
    const removeService = (index) => {
        setConfig(prev => ({
            ...prev,
            siteInfo: {
                ...prev.siteInfo,
                services: (prev.siteInfo.services || []).filter((_, i) => i !== index)
            }
        }));
    };
    // Helper to convert comma-separated strings back to arrays for output
    const getProcessedConfig = (rawConfig) => {
        // Use a simple spread/map to avoid full deep clone where unnecessary, 
        // but enough to safely modify for output
        const processed = {
            ...rawConfig,
            siteInfo: {
                ...rawConfig.siteInfo,
                services: (rawConfig.siteInfo.services || []).map((service) => ({
                    ...service
                }))
            },
            routes: (rawConfig.routes || []).map((route) => ({
                ...route,
                keywords: typeof route.keywords === 'string'
                    ? route.keywords.split(',').map((k) => k.trim()).filter((k) => k.length > 0)
                    : route.keywords
            }))
        };
        return processed;
    };
    const handleSave = () => {
        if (!isFormValid) {
            alert('Please fill in all required fields correctly before saving.');
            return;
        }
        onSave?.(getProcessedConfig(config));
    };
    const handleAiRecommendations = async (routeIndex) => {
        if (debug)
            console.log('handleAiRecommendations called with routeIndex:', routeIndex);
        setCurrentRouteIndex(routeIndex);
        setAiLoading(true);
        setAiModalOpen(true);
        setAcceptTitle(false);
        setAcceptKeywords(false);
        setAcceptDescription(false);
        try {
            const geminiService = createGeminiApiService('dummy-key'); // API key handled server-side
            const route = config.routes[routeIndex];
            const result = await geminiService.generateRouteRecommendations({
                route,
                siteInfo: config.siteInfo,
                baseUrl: config.siteInfo.url
            });
            if (result.success && result.data) {
                setAiRecommendations(result.data);
            }
            else {
                setAiRecommendations({ error: result.error || 'Failed to generate recommendations' });
            }
        }
        catch (error) {
            console.error('AI recommendation error:', error);
            setAiRecommendations({ error: 'Failed to generate AI recommendations' });
        }
        finally {
            setAiLoading(false);
        }
    };
    const handleAcceptAiRecommendations = () => {
        if (currentRouteIndex === null || !aiRecommendations)
            return;
        const updates = {};
        if (acceptTitle && aiRecommendations.title) {
            updates.title = aiRecommendations.title;
        }
        if (acceptKeywords && aiRecommendations.keywords) {
            updates.keywords = aiRecommendations.keywords;
        }
        if (acceptDescription && aiRecommendations.description) {
            updates.description = aiRecommendations.description;
        }
        if (Object.keys(updates).length > 0) {
            setConfig(prev => ({
                ...prev,
                routes: prev.routes.map((route, i) => i === currentRouteIndex ? { ...route, ...updates } : route)
            }));
        }
        setAiModalOpen(false);
        setAiRecommendations(null);
        setCurrentRouteIndex(null);
    };
    return (_jsxs("div", { className: "config-builder", children: [_jsx("h2", { children: "Config Builder" }), _jsxs("div", { className: "file-upload-section", children: [_jsx("label", { htmlFor: "config-file-upload", className: "file-upload-label", children: "Load Configuration File:" }), _jsx("input", { id: "config-file-upload", type: "file", accept: ".json", onChange: handleFileUpload, className: "file-upload-input" })] }), _jsx(Tab, { tabs: [
                    {
                        id: 'siteinfo',
                        label: 'Site Info',
                        content: (_jsxs("div", { className: "site-info-section", children: [_jsx(FormEngine, { formData: formData, onSubmitHandler: handleFormSubmit, name: "siteinfo", id: "siteinfo" }), _jsxs("div", { className: "address-section", children: [_jsx("h3", { children: "Address" }), _jsxs("div", { className: "field-group", children: [_jsx("label", { htmlFor: "street-address", children: "Street Address" }), _jsx("input", { id: "street-address", type: "text", placeholder: "Street Address", value: config.siteInfo.address?.streetAddress || '', onChange: (e) => updateAddress('streetAddress', e.target.value) })] }), _jsxs("div", { className: "field-group", children: [_jsx("label", { htmlFor: "city", children: "City" }), _jsx("input", { id: "city", type: "text", placeholder: "City", value: config.siteInfo.address?.addressLocality || '', onChange: (e) => updateAddress('addressLocality', e.target.value) })] }), _jsxs("div", { className: "field-group", children: [_jsx("label", { htmlFor: "state-region", children: "State/Region" }), _jsx("input", { id: "state-region", type: "text", placeholder: "State/Region", value: config.siteInfo.address?.addressRegion || '', onChange: (e) => updateAddress('addressRegion', e.target.value) })] }), _jsxs("div", { className: "field-group", children: [_jsx("label", { htmlFor: "postal-code", children: "Postal Code" }), _jsx("input", { id: "postal-code", type: "text", placeholder: "Postal Code", value: config.siteInfo.address?.postalCode || '', onChange: (e) => updateAddress('postalCode', e.target.value) })] }), _jsxs("div", { className: "field-group", children: [_jsx("label", { htmlFor: "country", children: "Country" }), _jsx("input", { id: "country", type: "text", placeholder: "Country", value: config.siteInfo.address?.addressCountry || '', onChange: (e) => updateAddress('addressCountry', e.target.value) })] })] }), _jsxs("div", { className: "social-links-section", children: [_jsx("h3", { children: "Social Links" }), socialLinks.map((link, index) => (_jsxs("div", { className: "field-group social-link-item", children: [_jsx("input", { type: "url", placeholder: "https://social-link.com", value: link, onChange: (e) => updateSocialLink(index, e.target.value) }), _jsx("button", { type: "button", onClick: () => removeSocialLink(index), children: "Remove" })] }, index))), _jsx("button", { type: "button", onClick: addSocialLink, children: "Add Social Link" })] })] }))
                    },
                    {
                        id: 'routes',
                        label: 'Routes',
                        content: (_jsx(FormValidationProvider, { children: _jsxs("div", { className: "routes-section", children: [_jsx("div", { className: "routes-list", children: config.routes.map((route, index) => (_jsxs("div", { className: "route-item", children: [routesForm.fields.map((field) => {
                                                    const Component = FC[field.component];
                                                    if (!Component)
                                                        return null;
                                                    let fieldValue = route[field.props.name];
                                                    if (field.props.name === 'keywords' && Array.isArray(fieldValue)) {
                                                        fieldValue = fieldValue.join(', ');
                                                    }
                                                    const fieldProps = {
                                                        ...field.props,
                                                        id: `${field.props.id}-${index}`,
                                                        ...(field.component === 'FormTextarea'
                                                            ? { defaultValue: fieldValue || '' }
                                                            : field.props.type === 'checkbox'
                                                                ? { checked: fieldValue || false }
                                                                : { value: fieldValue || '' }),
                                                        onChange: (e) => {
                                                            let value;
                                                            // Handle both direct values and event objects
                                                            const actualValue = (e && typeof e === 'object' && e.target) ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
                                                            if (field.props.name === 'keywords' && typeof actualValue === 'string') {
                                                                value = actualValue.split(',').map((s) => s.trim()).filter((s) => s);
                                                            }
                                                            else {
                                                                value = actualValue;
                                                            }
                                                            updateRoute(index, field.props.name, value);
                                                        }
                                                    };
                                                    return _jsx(Component, { ...fieldProps }, fieldProps.id);
                                                }), _jsxs("div", { className: "route-buttons", children: [_jsxs("button", { onClick: () => {
                                                                if (debug)
                                                                    console.log('AI Recommend button clicked for route:', index);
                                                                handleAiRecommendations(index);
                                                            }, className: "route-button ai-recommend", children: [_jsx("span", { className: "ai-icon", role: "img", "aria-label": "sparkles", children: "\u2728" }), " Recommend"] }), _jsx("button", { onClick: () => removeRoute(index), className: "route-button remove", children: "Remove" })] })] }, index))) }), _jsx("button", { onClick: addRoute, children: "Add Route" })] }) }))
                    },
                    {
                        id: 'services',
                        label: 'Services',
                        content: (_jsx(FormValidationProvider, { children: _jsxs("div", { className: "routes-section", children: [_jsx("div", { className: "routes-list", children: (config.siteInfo.services || []).map((service, index) => (_jsxs("div", { className: "route-item", children: [servicesForm.fields.map((field) => {
                                                    const Component = FC[field.component];
                                                    if (!Component)
                                                        return null;
                                                    const fieldValue = service[field.props.name];
                                                    const fieldProps = {
                                                        ...field.props,
                                                        id: `${field.props.id}-${index}`,
                                                        ...(field.component === 'FormTextarea'
                                                            ? { defaultValue: fieldValue || '' }
                                                            : { value: fieldValue || '' }),
                                                        onChange: (e) => {
                                                            // Handle both direct values and event objects
                                                            const actualValue = (e && typeof e === 'object' && e.target) ? e.target.value : e;
                                                            updateService(index, field.props.name, actualValue);
                                                        }
                                                    };
                                                    return _jsx(Component, { ...fieldProps }, fieldProps.id);
                                                }), _jsx("div", { className: "route-buttons", children: _jsx("button", { onClick: () => removeService(index), className: "route-button remove", children: "Remove" }) })] }, index))) }), _jsx("button", { onClick: addService, children: "Add Service" })] }) }))
                    },
                    {
                        id: 'visualdesign',
                        label: 'Visual Design',
                        content: (_jsx("div", { className: "visual-design-section", children: _jsx(FormEngine, { formData: visualFormData, name: "visualdesign", id: "visualdesign" }) }))
                    }
                ], orientation: "top" }), _jsx("button", { onClick: handleSave, disabled: !isFormValid, className: isFormValid ? 'save-button-valid' : 'save-button-invalid', children: "Save Config" }), !isFormValid && (_jsx("div", { className: "validation-message", children: "Please fill in all required fields (marked with *) before saving." })), _jsx(Accordion, { items: [
                    {
                        title: 'Configuration Preview',
                        content: _jsx("pre", { children: (() => {
                                try {
                                    return JSON.stringify(getProcessedConfig(config), null, 2);
                                }
                                catch (e) {
                                    // Simple fallback that doesn't try to analyze the object deeply
                                    const errorMessage = e instanceof Error ? e.message : String(e);
                                    return `Configuration contains non-serializable data (functions, circular references, or DOM elements).\n\nError: ${errorMessage}\n\nTo debug, check the config object in browser dev tools for functions or complex objects.`;
                                }
                            })() })
                    }
                ] }), _jsx(Modal, { modalID: "ai-recommendations", isOpen: aiModalOpen, handleCloseEvent: () => setAiModalOpen(false), modalContent: _jsxs("div", { className: "ai-recommendations-modal", children: [_jsx("h3", { children: "AI SEO Recommendations" }), currentRouteIndex !== null && (_jsxs("p", { children: [_jsx("strong", { children: "Route:" }), " ", config.routes[currentRouteIndex].name || config.routes[currentRouteIndex].path] })), aiLoading ? (_jsxs("div", { className: "ai-loading", children: [_jsx("p", { children: "Generating AI recommendations..." }), _jsx("div", { className: "loading-spinner" })] })) : aiRecommendations?.error ? (_jsx("div", { className: "ai-error", children: _jsxs("p", { children: ["Error: ", aiRecommendations.error] }) })) : aiRecommendations ? (_jsxs("div", { className: "ai-recommendations", children: [_jsxs("div", { className: "recommendation-item", children: [_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: acceptTitle, onChange: (e) => setAcceptTitle(e.target.checked) }), _jsx("strong", { children: "Title:" })] }), _jsxs("div", { className: "recommendation-content", children: [_jsx("div", { className: "current-value", children: _jsxs("small", { children: ["Current: ", config.routes[currentRouteIndex]?.title || 'None'] }) }), _jsx("div", { className: "suggested-value", children: aiRecommendations.title })] })] }), _jsxs("div", { className: "recommendation-item", children: [_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: acceptKeywords, onChange: (e) => setAcceptKeywords(e.target.checked) }), _jsx("strong", { children: "Keywords:" })] }), _jsxs("div", { className: "recommendation-content", children: [_jsx("div", { className: "current-value", children: _jsxs("small", { children: ["Current: ", (() => {
                                                                const keywords = config.routes[currentRouteIndex]?.keywords;
                                                                if (Array.isArray(keywords)) {
                                                                    return keywords.join(', ') || 'None';
                                                                }
                                                                else if (typeof keywords === 'string') {
                                                                    return keywords || 'None';
                                                                }
                                                                return 'None';
                                                            })()] }) }), _jsx("div", { className: "suggested-value", children: aiRecommendations.keywords?.join(', ') })] })] }), _jsxs("div", { className: "recommendation-item", children: [_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: acceptDescription, onChange: (e) => setAcceptDescription(e.target.checked) }), _jsx("strong", { children: "Description:" })] }), _jsxs("div", { className: "recommendation-content", children: [_jsx("div", { className: "current-value", children: _jsxs("small", { children: ["Current: ", config.routes[currentRouteIndex]?.description || 'None'] }) }), _jsx("div", { className: "suggested-value", children: aiRecommendations.description })] })] })] })) : null, _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: () => setAiModalOpen(false), children: "Cancel" }), _jsx("button", { onClick: handleAcceptAiRecommendations, disabled: !acceptTitle && !acceptKeywords && !acceptDescription, className: "accept-button", children: "Accept Selected" })] })] }) })] }));
}
